import React, { useState, useRef, useEffect } from "react";
import {
  Box,
  TextField,
  MenuItem,
  Popper,
  Typography,
  CircularProgress,
  InputAdornment,
  IconButton,
  Paper,
  Grow,
  alpha,
} from "@mui/material";
import { Search as SearchIcon, Close as CloseIcon } from "@mui/icons-material";

const SearchableSelect = ({
  options = [],
  value = null,
  onChange,
  label = "",
  placeholder = "Search...",
  isLoading = false,
  disabled = false,
  error = false,
  helperText = "",
  required = false,
  fullWidth = true,
  size = "small",
  getOptionLabel = (option) => option?.label ?? "",
  getOptionValue = (option) => option?.value ?? option?.id ?? null,
  renderOption = null,
  noOptionsText = "No options found",
  onInputChange = null,
  inputValue = "",
}) => {
  const [anchorEl, setAnchorEl] = useState(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);
  const popperRef = useRef(null);

  const safeOptions = Array.isArray(options) ? options.filter(Boolean) : [];
  const displayValue = value ? getOptionLabel(value) : "";
  const inputDisplayValue = isFocused || isOpen ? searchTerm : displayValue;

  useEffect(() => {
    if (inputValue !== undefined && inputValue !== searchTerm) {
      setSearchTerm(inputValue);
    }
  }, [inputValue]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (popperRef.current && !popperRef.current.contains(event.target)) {
        const isInputClick = inputRef.current?.contains(event.target);
        if (!isInputClick) {
          setIsOpen(false);
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  const handleOpen = (event) => {
    setAnchorEl(event.currentTarget);
    setIsOpen(true);
  };

  const handleClose = () => {
    setIsOpen(false);
  };

  const handleSelect = (option) => {
    onChange(option);
    setSearchTerm("");
    setIsOpen(false);
    setIsFocused(false);
    if (onInputChange) {
      onInputChange("");
    }
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange(null);
    setSearchTerm("");
    if (onInputChange) {
      onInputChange("");
    }
    setIsOpen(false);
    setIsFocused(false);
  };

  const handleInputChange = (e) => {
    const newValue = e.target.value;
    setSearchTerm(newValue);

    if (!isOpen && !disabled) {
      setIsOpen(true);
    }

    if (onInputChange) {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
      debounceTimerRef.current = setTimeout(() => {
        onInputChange(newValue);
      }, 300);
    }
  };

  const handleFocus = (e) => {
    if (disabled) return;
    setIsFocused(true);
    setAnchorEl(e.currentTarget);
    setIsOpen(true);
    setSearchTerm("");
    if (onInputChange) {
      onInputChange("");
    }
  };

  const handleBlur = (e) => {
    setTimeout(() => {
      if (
        popperRef.current &&
        !popperRef.current.contains(document.activeElement)
      ) {
        setIsOpen(false);
        setIsFocused(false);
        setSearchTerm("");
      }
    }, 200);
  };

  const filteredOptions = onInputChange
    ? safeOptions
    : safeOptions.filter((option) =>
        (getOptionLabel(option) || "")
          .toLowerCase()
          .includes(searchTerm.toLowerCase())
      );

  const open = Boolean(anchorEl) && isOpen;
  const selectedValue = value ? getOptionValue(value) : null;

  return (
    <Box sx={{ width: fullWidth ? "100%" : "auto", position: "relative" }}>
      <TextField
        ref={inputRef}
        fullWidth
        size={size}
        label={label}
        placeholder={isFocused ? placeholder : displayValue || placeholder}
        value={inputDisplayValue}
        onChange={handleInputChange}
        onFocus={handleFocus}
        onBlur={handleBlur}
        disabled={disabled}
        error={error}
        helperText={helperText}
        required={required}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon sx={{ color: "#94A3B8", fontSize: 18 }} />
            </InputAdornment>
          ),
          endAdornment: (
            <InputAdornment position="end">
              {isLoading ? (
                <CircularProgress size={20} color="primary" />
              ) : searchTerm || value ? (
                <IconButton size="small" onClick={handleClear} edge="end">
                  <CloseIcon sx={{ fontSize: 16, color: "#94A3B8" }} />
                </IconButton>
              ) : null}
            </InputAdornment>
          ),
        }}
        sx={{
          "& .MuiOutlinedInput-root": {
            borderRadius: "10px",
            backgroundColor: disabled ? "#F1F5F9" : "#FFFFFF",
            "&:hover fieldset": {
              borderColor: error ? "#EF4444" : "#3B82F6",
            },
            "& fieldset": {
              borderColor: error ? "#EF4444" : "#E2E8F0",
            },
            "&.Mui-focused fieldset": {
              borderColor: error ? "#EF4444" : "#3B82F6",
              borderWidth: 2,
            },
          },
          "& .MuiInputLabel-root": {
            fontSize: "0.75rem",
            fontWeight: 500,
            color: "#475467",
            "&.Mui-focused": {
              color: error ? "#EF4444" : "#3B82F6",
            },
          },
        }}
      />

      <Popper
        open={open}
        anchorEl={inputRef.current}
        placement="bottom-start"
        transition
        modifiers={[
          { name: "offset", options: { offset: [0, 4] } },
          {
            name: "sameWidth",
            enabled: true,
            fn: ({ state }) => {
              state.styles.popper.width = `${state.rects.reference.width}px`;
            },
            phase: "beforeWrite",
            requires: ["computeStyles"],
          },
        ]}
        style={{ zIndex: 9999 }}
        ref={popperRef}
      >
        {({ TransitionProps }) => (
          <Grow {...TransitionProps} timeout={200}>
            <Paper
              elevation={0}
              sx={{
                borderRadius: "10px",
                border: "1px solid #E2E8F0",
                boxShadow: "0 4px 20px rgba(0,0,0,0.08)",
                maxHeight: 280,
                overflow: "hidden",
                width: "100%",
              }}
            >
              {isLoading ? (
                <Box sx={{ display: "flex", justifyContent: "center", py: 3 }}>
                  <CircularProgress size={24} color="primary" />
                </Box>
              ) : filteredOptions.length === 0 ? (
                <Typography
                  sx={{
                    px: 2,
                    py: 2,
                    color: "#94A3B8",
                    fontSize: 13,
                    textAlign: "center",
                    bgcolor: "#FAFBFC",
                  }}
                >
                  {noOptionsText}
                </Typography>
              ) : (
                <Box sx={{ maxHeight: 280, overflow: "auto", py: 1 }}>
                  {filteredOptions.map((option, idx) => {
                    const optVal = getOptionValue(option);
                    const isSelected =
                      selectedValue != null && optVal === selectedValue;
                    return (
                      <MenuItem
                        key={optVal ?? `opt-${idx}`}
                        onClick={() => handleSelect(option)}
                        selected={isSelected}
                        sx={{
                          py: 1.5,
                          px: 2,
                          fontSize: "0.875rem",
                          color: isSelected ? "#3B82F6" : "#0F172A",
                          fontWeight: isSelected ? 500 : 400,
                          backgroundColor: isSelected
                            ? alpha("#3B82F6", 0.06)
                            : "transparent",
                          "&:hover": {
                            backgroundColor: isSelected
                              ? alpha("#3B82F6", 0.06)
                              : alpha("#3B82F6", 0.04),
                          },
                          "&.Mui-selected": {
                            backgroundColor: alpha("#3B82F6", 0.06),
                            "&:hover": {
                              backgroundColor: alpha("#3B82F6", 0.08),
                            },
                          },
                        }}
                      >
                        {renderOption
                          ? renderOption(option)
                          : getOptionLabel(option)}
                      </MenuItem>
                    );
                  })}
                </Box>
              )}
            </Paper>
          </Grow>
        )}
      </Popper>
    </Box>
  );
};

export default SearchableSelect;